'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function DolgununDuygusalEtkileriPage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/dolgunun-duygusal-etkileri-mecazi';
  const pageTitle = 'Dolgunun Duygusal Etkileri (Mecazi)';
  const pageDescription = 'Dolgu yalnızca yüz hatlarını değil, duyguları da şekillendirir. Estetik müdahalelerin özgüven, özsaygı ve duygusal denge üzerindeki mecazi etkileri.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Dolgunun Duygusal Etkileri', href: '/tr/blog/dolgunun-duygusal-etkileri-mecazi' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Dolgunun duygusal etkileri ve mecazi anlamları"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-purple-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Duygusal Etki
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Dolgunun Duygusal Etkileri (Mecazi)
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>8 Şubat 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>7 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Dolgu uygulamaları genellikle yüz hatlarını yeniden dengelemek, hacim kaybını gidermek ve genç bir görünüm sağlamak amacıyla yapılır. Ancak her estetik müdahalede olduğu gibi, dolgu da yalnızca bedensel bir işlem değildir — aynı zamanda duygusal bir yenilenme deneyimidir.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Kimi için dolgu, aynada yeniden kendini bulmak; kimi içinse geçmişin yorgunluğunu silip geleceğe daha güvenli bakmaktır. Bu yüzden dolgunun etkisi yalnızca cilt altında değil, bazen kalp ritminde hissedilir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Dolgu: Boşlukları Doldurmak, Dengeyi Bulmak
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Estetikte dolgu, yüzün kaybettiği hacmi geri kazandırır. Mecazi anlamda ise;
              </p>

              <div className="bg-purple-50 rounded-xl p-6 mb-6">
                <p className="text-purple-800 font-medium text-lg italic">
                  "Dolgu, yüzün değil, benliğin boşluklarını doldurur."
                </p>
              </div>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Yorgun, çökmüş bir yüz görünümü çoğu zaman kişinin içsel enerjisini de etkiler. Dolgu sonrası daha dolgun dudaklar, belirgin elmacık kemikleri veya toparlanmış yüz hatları — beynin "yenilenme" merkezini (limbik sistem) uyarır.
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <p className="text-blue-800 font-medium">
                  Bu da kişiye duygusal bütünlük ve güven hissi kazandırır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Estetik Psikoloji: Aynada Kendini Yeniden Görmek
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Yüz, insanın kimlik aynasıdır. Klinik psikolojide, "ayna benliği" kavramı bireyin kendilik algısının temelini oluşturur.
              </p>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Dolgu işlemleriyle kişi;
              </p>

              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-green-800 space-y-2">
                  <li>Kendini yeniden tanır,</li>
                  <li>Yorgun veya üzgün yüz ifadesinden kurtulur,</li>
                  <li>Ve "ben hâlâ aynıyım, sadece daha dinç görünürüm" duygusunu yaşar.</li>
                </ul>
                <p className="text-green-800 mt-3 font-medium">
                  💡 Sonuç: Dolgu, yalnızca estetik değil — duygusal olarak da "yeniden doğma" ritüelidir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Denge Sanatı: Fazla Değil, Doğru Dolgu
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Fazla dolgu yüzü "yabancı" hale getirir. Bu noktada, dolgunun mecazi anlamı devreye girer:
              </p>

              <div className="bg-orange-50 rounded-xl p-6 mb-6">
                <p className="text-orange-800 font-medium text-lg italic">
                  "Kendine dokun ama kendini kaybetme."
                </p>
              </div>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Doğal oranda yapılan dolgu;
              </p>

              <div className="bg-indigo-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-indigo-800 space-y-2">
                  <li>Kişinin yüz kimliğini korur,</li>
                  <li>Estetik dengeyle duygusal dengeyi aynı noktada buluşturur.</li>
                </ul>
                <p className="text-indigo-800 mt-3 font-medium">
                  Yani dolgunun asıl gücü, abartısız dönüşümündedir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Duygusal Etkilerin Bilimsel Arka Planı
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Araştırmalar gösteriyor ki estetik uygulamalar yalnızca fiziksel değil, nöropsikolojik tatmin sağlar.
              </p>
              
              {/* Table */}
              <div className="overflow-x-auto mb-6">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Etki Alanı
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Sonuç
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Açıklama
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Dopamin (ödül hormonu)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">↑</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Estetik memnuniyet motivasyonu artırır</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Kortizol (stres hormonu)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">↓</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Özalgı güçlenmesiyle stres azalır</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Sosyal etkileşim</td>
                      <td className="px-6 py-4 text-sm text-gray-900">↑</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Yüz ifadesi değiştikçe beden dili güçlenir</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-rose-50 rounded-xl p-6 mb-6">
                <p className="text-rose-800 font-medium">
                  💬 Kısacası: Dolgu yaptıran kişi yüzünü değil, beyninin duygusal kimyasını da tazeler.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Doğallık ve Empati Arasındaki Bağ
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Doğal dolgu uygulamaları, duyguların yüzle uyum içinde kalmasını sağlar. Yüzdeki aşırı mimik kaybı veya şişkinlik, kişiyi soğuk gösterir; oysa doğal oran, empatiyi güçlendirir.
              </p>

              <p className="text-gray-700 mb-4 leading-relaxed">
                İşte bu yüzden modern estetik tıp, artık "fazla dolgu" değil — duygusal uyum üzerine çalışıyor.
              </p>

              <div className="bg-yellow-50 rounded-xl p-6 mb-6">
                <p className="text-yellow-800 font-medium">
                  💎 Güzel yüz, doğru oranda mutlulukla parlar.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6. Dolgu Sonrası Ruh Halinde Değişim
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Birçok hasta işlem sonrası "kendimi daha pozitif hissediyorum" der. Bu etki yalnızca görsellikten değil, özgüvenin yeniden inşasından kaynaklanır.
              </p>

              <div className="bg-teal-50 rounded-xl p-6 mb-6">
                <p className="text-teal-800">
                  Beden dili açılır, gülümseme kolaylaşır, sosyal etkileşim artar. Mecazi olarak kişi artık "kapanmış yüzüyle değil, aydınlık benliğiyle" dünyaya bakar.
                </p>
              </div>

              {/* Image placeholder for article */}
              <div className="relative h-64 rounded-xl overflow-hidden mb-8">
                <Image
                  src="/images/terleme-tedavisi.webp"
                  alt="Dolgunun duygusal etkileri ve mecazi anlamları"
                  fill
                  className="object-cover"
                />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. Dolgu yaptırmak duygusal olarak nasıl hissettirir?</h3>
                  <p className="text-gray-700">Kendini daha taze, güçlü ve dengede hissetmek yaygındır.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. Dolgu sonrası depresif hissedilir mi?</h3>
                  <p className="text-gray-700">Hayır, genelde estetik memnuniyet dopamin artışıyla ruh halini pozitif etkiler.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. Dolgu kişiliği değiştirir mi?</h3>
                  <p className="text-gray-700">Kişilik değil ama özgüven ve sosyal iletişim biçimi güçlenebilir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">4. Mecazi olarak "dolgu" neyi temsil eder?</h3>
                  <p className="text-gray-700">Boşlukların tamamlanmasını, içsel eksikliklerin dengeye kavuşmasını.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">5. Doğal görünüm neden bu kadar önemli?</h3>
                  <p className="text-gray-700">Çünkü doğallık, hem fiziksel hem duygusal kimliği korur.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
                <li>Harvard Health Publishing (2023) – "The Psychology of Aesthetic Interventions"</li>
                <li>Aesthetic Medicine Journal (2024) – "Facial Fillers and Emotional Perception"</li>
                <li>Türk Estetik Tıp Derneği (TETD) – "Dolgu ve Ruhsal Etki Çalışması"</li>
                <li>ISAPS Global Report 2024 – "Patient Confidence and Non-Surgical Aesthetics"</li>
                <li>American Psychological Association (APA) – "Self-Perception After Cosmetic Enhancements"</li>
              </ul>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">#dolgu</span>
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">#duygusaletkiler</span>
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">#estetikpsikoloji</span>
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">#özgüven</span>
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">#yüzdenge</span>
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">#mecazianlam</span>
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">#hyaluronikasit</span>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-8 text-white text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Dolgu Hakkında Bilgi Alın</h3>
              <p className="mb-6">Dolgunun fiziksel ve duygusal etkileri hakkında uzman doktorumuzdan detaylı bilgi alın.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Dolgunun duygusal etkileri hakkında bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp ile İletişim
                </Link>
                <Link
                  href="/tr/iletisim"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-purple-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Randevu Al
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              href="/tr/blog/dolgu-islemleri-yuz-liftingi-saglar-mi"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Dolgu İşlemleri Yüz Liftingi Sağlar mı?</h4>
              <p className="text-gray-600 text-sm">Dolgu işlemlerinin yüz liftingi etkisi yaratıp yaratmadığı ve nasıl uygulandığı.</p>
            </Link>
            <Link
              href="/tr/blog/sivi-yuz-germe-nedir-hangi-icerikli-urunler-kullanilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Sıvı Yüz Germe Nedir?</h4>
              <p className="text-gray-600 text-sm">Sıvı yüz germe işlemi, hangi içerikler kullanılır ve nasıl uygulanır?</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
